import React, { useMemo, useState } from "react";
import classes from "../TransInter.module.scss";
import {
  Box,
  Button,
  Divider,
  Flex,
  Space,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import FromAccount from "@/components/common/from-account/FromAccount";
import ApprovalInfo from "../components/ApprovalInfo";
import FileBadge from "../components/FileBadge";
import OtpModal from "@/components/common/modal/otp-modal/OtpModal";
import SmartOtpInstructions from "@/components/common/modal/otp-modal/SmartOtpInstructions";
import { useDisclosure } from "@mantine/hooks";
import { useTransFormContext } from "../TransInterFormContext";
import { useTranslation } from "react-i18next";
import { TransInterStepFourProps } from "../types";
import { downloadFormDocument } from "../documentUtils";
import {
  useGetInternationalTransferMasterDataQuery,
  useGetIntlTransferPurposeQuery,
} from "@/services/internationalTransferAPI";
import { computeIntlDebit } from "../debitUtils";
import { getCurrencyScale, formatMoney, reformatMoneyDisplay } from "../currencyFormat";

// Dòng label - value trong khối thông tin.
const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  // Nếu set -> nhãn có bề rộng cố định, giá trị căn trái theo cột (giống design).
  // Bỏ trống -> giá trị căn phải (space-between).
  labelWidth?: number;
}> = ({ label, value, valueColor, labelWidth }) => (
  <Flex
    justify={labelWidth ? "flex-start" : "space-between"}
    align="center"
    gap={20}
  >
    <Text
      fz={14}
      c="#6B7280"
      style={labelWidth ? { width: labelWidth, flexShrink: 0 } : undefined}
    >
      {label}
    </Text>
    <Text fz={14} fw={600} c={valueColor || "#212936"}>
      {value}
    </Text>
  </Flex>
);

const TransInterStepFour: React.FC<TransInterStepFourProps> = ({
  onBackStep,
  sources,
  sourceLinkId,
  onChangeSourceLink,
  initData,
  approvalProcessInfo,
  onRequestOtp,
  onConfirmOtp,
  authType,
  onChangeAuthType,
}) => {
  const [isOpenedOTPModal, { open: openOTPModal, close: closeOTPModal }] =
    useDisclosure(false);
  // Smart OTP: mã sinh trên app Javis OTP (KeyPass), không gửi SMS/không có "gửi lại".
  const isSmartOtp = authType === "2";
  const form = useTransFormContext();
  const { t, i18n } = useTranslation();
  const [otp, setOtp] = useState("");
  const { data: masterData } = useGetInternationalTransferMasterDataQuery();
  const { data: purposeData } = useGetIntlTransferPurposeQuery();

  const purposeGroups = purposeData?.body?.purposeGroups || [];
  const feeTypes = masterData?.body?.feeTypes || [];

  // Chọn tên theo ngôn ngữ hiện tại (name = tiếng Việt, nameEn = tiếng Anh).
  const pickName = (o: { name: string; nameEn?: string }) =>
    (i18n.language || "vi").startsWith("en") && o.nameEn ? o.nameEn : o.name;

  /**
   * #55993: bước 4 trước đây in thẳng `feeTypes[].name` của backend — mà
   * `getInternationalTransferMasterData` gán cứng tiếng Việt (" Người chuyển trả phí",
   * " Đồng chi trả", " Người thụ hưởng trả phí") và KHÔNG có trường tiếng Anh.
   * Bước 1 vốn đã tự đặt nhãn cho đúng ba mã này bằng khoá i18n — dùng lại cùng bảng
   * để hai bước nói giống nhau. Mã lạ (nếu backend thêm sau) thì mới lùi về `name`.
   */
  const FEE_TYPE_KEY: Record<string, string> = {
    OUR: "SENDER_PAYS_FEE",
    SHA: "SHARED_FEE",
    BEN: "BENEFICIARY_PAYS_FEE",
  };
  const feeTypeLabel = useMemo(
    () => {
      const code = form.values.feeType;
      const key = FEE_TYPE_KEY[String(code ?? "")];
      if (key) return t(key);
      return feeTypes.find((f) => f.code === code)?.name || code || "—";
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feeTypes, form.values.feeType, i18n.language],
  );
  const purposeTypeLabel = useMemo(() => {
    const g = purposeGroups.find((p) => p.code === form.values.purposeType);
    return g ? pickName(g) : form.values.purposeType || "—";
  }, [purposeGroups, form.values.purposeType, i18n.language]);
  const purposeDetailLabel = useMemo(() => {
    const parent = purposeGroups.find((p) => p.code === form.values.purposeType);
    const item = parent?.items?.find(
      (it) => it.code === form.values.purposeDetail,
    );
    return item ? pickName(item) : form.values.purposeDetail || "—";
  }, [
    purposeGroups,
    form.values.purposeType,
    form.values.purposeDetail,
    i18n.language,
  ]);

  const currency = form.values.currency || "";
  const currencyScale = getCurrencyScale(currency);
  // Số chữ số thập phân hiển thị theo loại tiền (BR-06): USD/EUR… = 2, VND/JPY… = 0.
  const amountValue =
    Number(String(form.values.amount || "").replace(/[^\d.]/g, "")) || 0;
  const amountDisplay = initData?.amountDisplay
    ? reformatMoneyDisplay(initData.amountDisplay)
    : `${formatMoney(amountValue, currencyScale)} ${currency}`.trim();
  const feeDisplay = reformatMoneyDisplay(
    initData?.feeAmountForeignDisplay || initData?.feeAmountDisplay,
  );

  // Tổng số tiền ghi nợ — FE tự tính (nhất quán với giá trị gửi lên API init).
  const selectedSource = sources.find((s) => s.id === sourceLinkId);
  const totalDebitDisplay = useMemo(
    () =>
      computeIntlDebit({
        amount: form.values.amount,
        currency,
        feeType: form.values.feeType,
        foreignAmount: form.values.foreignAmount,
        needBuyCurrency: form.values.needBuyCurrency,
        accountCcy: selectedSource?.currency,
        currencies: masterData?.body?.currencies,
      }).display,
    [
      currency,
      masterData,
      selectedSource,
      form.values.amount,
      form.values.feeType,
      form.values.foreignAmount,
      form.values.needBuyCurrency,
    ],
  );
  const documents = form.values.documents || [];
  // Maker đã tích "Nợ chứng từ bổ sung sau" ở bước Hồ sơ (step 3).
  const debtLater = !!form.values.debtDocumentLater;

  const roInputStyles = {
    input: { backgroundColor: "#F9FAFB", color: "#212936" },
  };

  const handleConfirm = async () => {
    const ok = await onConfirmOtp?.(otp);
    if (ok) closeOTPModal();
    setOtp("");
  };

  const handleRequestOtp = async () => {
    const ok = await onRequestOtp?.();
    if (ok) openOTPModal();
  };

  return (
    <>
      <Flex gap={20} align="flex-start">
        {/* CỘT TRÁI */}
        <Box className={classes.sourceAccount} w="62%">
          <Text fw={600} fz={16} c="#212936">
            {t("INFORMATION_TRANSFER")}
          </Text>
          <Space h={16} />
          <FromAccount
            title=""
            sourceLinkId={sourceLinkId}
            sources={sources}
            onChangeSourceLinkId={onChangeSourceLink}
          />
          <Space h={20} />
          <Stack spacing={16}>
            <InfoRow
              label={t("TRANSACTION_AMOUNT")}
              value={amountDisplay}
              labelWidth={180}
            />
            <InfoRow label={t("FEE_TYPE")} value={feeTypeLabel} labelWidth={180} />
            <InfoRow label={t("FEE_AMOUNT")} value={feeDisplay} labelWidth={180} />
            <InfoRow
              label={t("TOTAL_DEBITED_AMOUNT")}
              value={totalDebitDisplay}
              valueColor="#2A81D0"
              labelWidth={180}
            />
          </Stack>
          <Space h={20} />
          <Divider />
          <Space h={20} />
          <Stack spacing={6}>
            <Text fz={14} c="#6B7280">
              {t("TRANSFER_CONTENT_FULL")}
            </Text>
            <Text fz={14} fw={500} c="#212936">
              {form.values.detailContent || "—"}
            </Text>
          </Stack>
          <Space h={16} />
          <Stack spacing={6}>
            <Text fz={14} c="#6B7280">
              {t("NOTICE_TO_THE_BANK")}
            </Text>
            <Text fz={14} fw={500} c="#212936">
              {form.values.bankNotice || "—"}
            </Text>
          </Stack>
          <Space h={20} />
          <Divider />
          <Space h={20} />
          <Text fw={600} fz={16} c="#212936">
            {t("BENEFICIARY_BANK")}
          </Text>
          <Space h={12} />
          <Flex gap={16} align="flex-end">
            <TextInput
              label={t("SWIFT_CODE")}
              w={139}
              radius={8}
              readOnly
              styles={roInputStyles}
              value={form.values.beneficiarySwiftCode || "—"}
            />
            <TextInput
              label={t("BANK_NAME")}
              w={200}
              radius={8}
              readOnly
              styles={roInputStyles}
              value={form.values.beneficiaryBankName || "—"}
            />
            <TextInput
              label={t("ADDRESS")}
              style={{ flex: 1 }}
              radius={8}
              readOnly
              styles={roInputStyles}
              value={form.values.beneficiaryBankAddress || "—"}
            />
          </Flex>
        </Box>

        {/* CỘT PHẢI */}
        <Box style={{ flex: 1 }}>
          <Box className={classes.sourceAccount}>
            <ApprovalInfo
              approvalProcessInfo={approvalProcessInfo}
              authObjects={initData?.authObjects}
              onChangeAuthType={onChangeAuthType}
            />
          </Box>
          <Space h={20} />
          <Box className={classes.sourceAccount}>
            <Text fw={600} fz={16} c="#212936">
              {t("DOCUMENTS_PROVIDED")}
            </Text>
            <Space h={16} />
            <InfoRow label={t("PURPOSE_TYPE")} value={purposeTypeLabel} />
            <Space h={14} />
            <InfoRow label={t("PURPOSE_OF_MONEY_TRANSFER")} value={purposeDetailLabel} />
            <Space h={18} />
            {/* Tiêu đề danh sách file + chip trạng thái "Nợ chứng từ bổ sung sau"
                (màu vàng, cùng hàng) khi maker tích nợ chứng từ ở bước Hồ sơ. */}
            <Flex justify="space-between" align="center" gap={12}>
              <Text fw={600} fz={14} c="#212936">
                {t("DOCUMENT_FILE_LIST")}
              </Text>
              {debtLater && (
                <Box
                  style={{
                    flexShrink: 0,
                    backgroundColor: "#FFF4D0",
                    color: "#991B1B",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("DOCUMENTS_OWED_LATER")}
                </Box>
              )}
            </Flex>
            <Space h={10} />
            <Stack spacing={12}>
              {documents.length === 0 && (
                <Text c="#6B7280" fz={14}>
                  —
                </Text>
              )}
              {documents.map((doc) => (
                <Flex
                  key={`${doc.documentName}-${doc.fileContent.slice(0, 12)}`}
                  justify="space-between"
                  align="center"
                >
                  <Flex gap={10} align="center">
                    <FileBadge fileType={doc.fileType} size={30} />
                    <Text fz={14} c="#157CC1">
                      {doc.documentName}
                    </Text>
                  </Flex>
                  <IconDownload
                    size={20}
                    color="#2A81D0"
                    style={{ cursor: "pointer" }}
                    onClick={() => downloadFormDocument(doc)}
                  />
                </Flex>
              ))}
            </Stack>
            {debtLater && (
              <>
                <Space h={16} />
                {/* Nút "Bổ sung chứng từ": thu gọn vừa với chữ (không full-width),
                    căn giữa; điều hướng quay lại bước Hồ sơ để đính kèm/sửa chứng từ. */}
                <Flex justify="center">
                  <Button
                    variant="outline"
                    leftIcon={<IconPlus size={16} />}
                    onClick={onBackStep}
                    sx={() => ({
                      width: "fit-content",
                      height: 32,
                      borderRadius: 8,
                      padding: "0 12px",
                      borderColor: "#005F9E",
                      color: "#005F9E",
                      fontWeight: 600,
                      fontSize: 13,
                    })}
                  >
                    {t("SUPPLEMENT_DOCUMENTS")}
                  </Button>
                </Flex>
              </>
            )}
          </Box>
        </Box>
      </Flex>

      <Space h={80} />

      {/* Footer cố định */}
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: "calc(var(--mantine-navbar-width, 0px) + 20px)",
          right: 0,
          padding: "10px 24px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0px 5px 15px 0px #00000026",
          borderRadius: "12px",
          height: "76px",
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <Flex gap={10}>
          <Button
            onClick={onBackStep}
            sx={() => ({
              width: "100px",
              backgroundColor: "#F4F4F6",
              color: "#525866",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
              "&:hover": { backgroundColor: "#F4F4F6" },
            })}
          >
            {t("BACK") || t("GO_BACK")}
          </Button>
          <Button
            type="submit"
            onClick={handleRequestOtp}
            sx={() => ({
              width: "160px",
              backgroundColor: "#2A81D0",
              color: "white",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
            })}
          >
            {t("CONFIRM") || t("CONFIRM")}
          </Button>
        </Flex>
      </Box>

      <OtpModal
        opened={isOpenedOTPModal}
        onClose={closeOTPModal}
        onConfirm={handleConfirm}
        // Smart OTP: mã KeyPass hết hạn sau 90s (bám Figma); SMS giữ 60s
        expireTime={isSmartOtp ? 90 : 60}
        otp={otp}
        onChangeOtp={(value) => setOtp(value)}
        authType={authType}
        // Smart OTP: mã sinh trên app KeyPass, không có "gửi lại" (ẩn hẳn link resend)
        handleSendOtp={
          isSmartOtp
            ? undefined
            : async () => {
                await onRequestOtp?.();
              }
        }
        hideResend={isSmartOtp}
        modalTitle={isSmartOtp ? String(t("SMART_OTP_MODAL_TITLE")) : undefined}
        modalDescription={isSmartOtp ? t("SMART_OTP_MODAL_DESC") : undefined}
        // Hộp hướng dẫn 3 bước + nhãn đếm ngược/nút xác nhận riêng cho Smart OTP
        beforeInput={isSmartOtp ? <SmartOtpInstructions /> : undefined}
        countdownLabel={
          isSmartOtp ? String(t("SMART_OTP_EXPIRE_LABEL")) : undefined
        }
        showCountdownInSeconds={isSmartOtp}
        confirmLabel={isSmartOtp ? String(t("CONFIRM")) : undefined}
      />
    </>
  );
};

export default TransInterStepFour;
